import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Stack, HStack, Button, useToast } from "@chakra-ui/react";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import TypeaheadWidget from "../../../CommonComponents/TypeaheadWidget";
import ImageWidget from "../../../CommonComponents/ImageWidget";
import { getDeletedValues, removeKeysFromFormData } from "../../../util/helper";
import { getResearchCategoriesAPI } from "../../../services/ResearchCategories";
import {
  getPersonalityBasicData,
  updatePersonalityBasicData,
} from "../../../services/Personality";
import { fetchMasterlistSubTypes } from "../../../util/getMasterlistSubType";
import FormLoader from "../../../CommonComponents/FormLoader";
import {
  getApiErrorMessage,
  showErrorToast,
  showSuccessToast,
} from "../../../util/errorHandler";
import {
  homeCollageOrderSchema,
  homeCollageOrderUiSchema,
} from "../../../constants/homeCollage";
import { parseHomeCollageConflict } from "../../../util/homeCollageConflict";
import { useHomeCollageReassign } from "../../../hooks/useHomeCollageReassign";
import HomeCollageReassignDialog from "../../../CommonComponents/HomeCollageReassignDialog";
import {
  exploreCarouselOrderSchema,
  exploreCarouselOrderUiSchema,
} from "../../../constants/exploreCarousel";
import {
  exploreSearchHintOrderSchema,
  exploreSearchHintOrderUiSchema,
  exploreSearchHintKindSchema,
  exploreSearchHintKindUiSchema,
  useSearchHintPoolSize,
} from "../../../constants/exploreSearchHints";
// ─── Left Column Schema – Identity & Classification ──────────────────────────
/**
 * Returns the JSON schema for the left column (identity/classification fields).
 * @param {boolean} hasIsExplore - Whether the isExplore flag is enabled.
 * @returns {object} JSON Schema object for left column.
 *
 * Scale Analysis: Field list is static; schema is recomputed only when
 * hasIsExplore changes, making it O(1) per render regardless of data volume.
 */
const getLeftColumnSchema = (hasIsExplore) => {
  const baseProperties = {
    alias: {
      type: "string",
      title: "Alias",
    },
    meta_title: {
      type: "string",
      title: "Meta Title",
    },
    meta_description: {
      type: "string",
      title: "Meta Description",
    },
    first_name: {
      type: "string",
      title: "First Name",
    },
    last_name: {
      type: "string",
      title: "Last Name",
    },
    full_name: {
      type: "string",
      title: "Full Name",
    },
    image_alias: {
      type: ["string", "null"],
      title: "Image",
    },
    image_alias_preview: {
      type: ["string", "null"],
      title: "Image Preview",
    },
    subject: {
      type: "string",
      title: "Masterlist Type",
    },
    masterlist_type: {
      type: "string",
      title: "Masterlist Sub Type",
    },
    researchCategories: {
      type: "array",
      title: "Research Category",
      items: {
        type: "object",
        properties: {
          alias: {
            type: "string",
            title: "Alias",
          },
        },
      },
    },
  };

  return {
    title: "Primary fields",
    description: "",
    type: "object",
    properties: baseProperties,
    required: ["alias", "full_name", "subject", "masterlist_type"],
  };
};

// ─── Right Column Schema – Metadata & Explore Fields ─────────────────────────
/**
 * Returns the JSON schema for the right column (metadata/explore fields).
 * @param {boolean} hasIsExplore - Whether the isExplore flag is enabled.
 * @returns {object} JSON Schema object for right column.
 */
const getRightColumnSchema = (hasIsExplore) => {
  const baseProperties = {
    aka: {
      type: ["array", "null"],
      title: "AKA",
      items: { type: "string" },
    },
    aka_typo_tolerance: {
      type: ["array", "null"],
      title: "AKA Typo Tolerance",
      items: { type: "string" },
    },
    keywords: {
      type: ["array", "null"],
      title: "Keywords",
      items: { type: "string" },
    },
    has_scholarship: {
      type: "boolean",
      title: "Has Scholarship",
      default: false,
    },
    integrated_timeline_default_view: {
      type: "string",
      title: "Integrated Timeline Default View",
      enum: ["full", "lite"],
      default: "lite",
    },
    isExplore: {
      type: "boolean",
      title: "Is Explore",
      default: false,
    },
  };

  // Conditionally inject isExplore-dependent fields
  if (hasIsExplore) {
    baseProperties["display_name"] = {
      type: "string",
      title: "Display Name",
    };
    baseProperties["sort_name"] = {
      type: ["string", "null"],
      title: "Sort Name",
    };
    baseProperties["route_type"] = {
      type: "string",
      title: "Route",
    };
    baseProperties["image"] = {
      type: "string",
      title: "Explore Image URL",
    };
    baseProperties["home_collage_order"] = homeCollageOrderSchema();
    baseProperties["explore_carousel_order"] = exploreCarouselOrderSchema();
    baseProperties["explore_search_hint_order"] =
      exploreSearchHintOrderSchema();
    baseProperties["explore_search_hint_kind"] = exploreSearchHintKindSchema();
  }

  const requiredFields = [];
  if (hasIsExplore) {
    requiredFields.push("route_type");
    requiredFields.push("image");
  }

  return {
    title: "",
    description: "",
    type: "object",
    properties: baseProperties,
    required: requiredFields,
  };
};

// ─── Combined Schema – Single Column (Preview Mode) ───────────────────────────
/**
 * Merges left and right schemas into a single combined schema for preview mode.
 * Reuses getLeftColumnSchema and getRightColumnSchema to avoid duplication.
 * @param {boolean} hasIsExplore - Whether the isExplore flag is enabled.
 * @returns {object} Merged JSON Schema object.
 */
const getCombinedSchema = (hasIsExplore) => {
  const leftSchema = getLeftColumnSchema(hasIsExplore);
  const rightSchema = getRightColumnSchema(hasIsExplore);

  return {
    title: "Primary fields",
    description: "",
    type: "object",
    properties: {
      ...leftSchema.properties,
      ...rightSchema.properties,
    },
    required: [...leftSchema.required, ...rightSchema.required],
  };
};

// ─── Left Column UI Schema ────────────────────────────────────────────────────
/**
 * Curried function: returns left column UI schema configuration.
 * @param {boolean} hasIsExplore
 * @returns {function} (isSubmitting: boolean) => uiSchema object
 */
const getLeftColumnUiSchema = (hasIsExplore) => (isSubmitting) => ({
  "ui:submitButtonOptions": { norender: true },
  "ui:style": { fontSize: "14px" },
  researchCategories: {
    items: {
      alias: { "ui:widget": "typeahead1" },
    },
  },
  image_alias_preview: {
    "ui:widget": ImageWidget,
    "ui:options": { "ui:emptyValue": null },
  },
  full_name: { "ui:emptyValue": "", "ui:readonly": true },
  alias: { "ui:emptyValue": "", "ui:readonly": true },
  meta_title: { "ui:emptyValue": "" },
  meta_description: { "ui:widget": "textarea", "ui:emptyValue": "" },
  image_alias: { "ui:emptyValue": "" },
  subject: { "ui:emptyValue": "" },
  masterlist_type: { "ui:widget": "typeahead3", "ui:emptyValue": "" },
});

// ─── Right Column UI Schema ───────────────────────────────────────────────────
/**
 * Curried function: returns right column UI schema configuration.
 * Conditionally adds isExplore-dependent field entries.
 * @param {boolean} hasIsExplore
 * @returns {function} (isSubmitting: boolean) => uiSchema object
 */
const getRightColumnUiSchema = (hasIsExplore) => (isSubmitting) => {
  const baseSchema = {
    "ui:submitButtonOptions": { norender: true },
    "ui:style": { fontSize: "14px" },
    keywords: { "ui:emptyValue": "" },
    aka_typo_tolerance: { "ui:emptyValue": "" },
    aka: { "ui:emptyValue": "" },
    has_scholarship: { "ui:emptyValue": false },
    integrated_timeline_default_view: {
      "ui:widget": "radio",
      "ui:emptyValue": "lite",
      "ui:enumNames": ["Full", "Lite"],
    },
    isExplore: { "ui:emptyValue": false },
  };

  if (hasIsExplore) {
    baseSchema.display_name = { "ui:emptyValue": "" };
    baseSchema.sort_name = { "ui:emptyValue": "" };
    baseSchema.route_type = { "ui:emptyValue": "" };
    baseSchema.image = { "ui:emptyValue": "" };
    baseSchema.home_collage_order = homeCollageOrderUiSchema();
    baseSchema.explore_carousel_order = exploreCarouselOrderUiSchema();
    baseSchema.explore_search_hint_order = exploreSearchHintOrderUiSchema();
    baseSchema.explore_search_hint_kind = exploreSearchHintKindUiSchema();
  }

  return baseSchema;
};

// ─── Combined UI Schema – Preview Mode ───────────────────────────────────────
/**
 * Merges left and right UI schemas for single-column preview rendering.
 * Leverages getLeftColumnUiSchema and getRightColumnUiSchema to avoid duplication.
 * @param {boolean} hasIsExplore
 * @returns {function} (isSubmitting: boolean) => uiSchema object
 */
const getCombinedUiSchema = (hasIsExplore) => (isSubmitting) => {
  const leftUi = getLeftColumnUiSchema(hasIsExplore)(isSubmitting);
  const rightUi = getRightColumnUiSchema(hasIsExplore)(isSubmitting);

  return {
    ...leftUi,
    ...rightUi,
    "ui:submitButtonOptions": { norender: true },
    "ui:style": { fontSize: "14px" },
  };
};

// ─── Field Split Definitions ──────────────────────────────────────────────────
const LEFT_FIELDS = [
  "alias",
  "meta_title",
  "meta_description",
  "first_name",
  "last_name",
  "full_name",
  "image_alias",
  "image_alias_preview",
  "subject",
  "masterlist_type",
  "researchCategories",
];
const RIGHT_FIELDS = [
  "aka",
  "aka_typo_tolerance",
  "keywords",
  "has_scholarship",
  "integrated_timeline_default_view",
  "isExplore",
  "display_name",
  "sort_name",
  "route_type",
  "image",
  "home_collage_order",
  "explore_carousel_order",
  "explore_search_hint_order",
  "explore_search_hint_kind",
];

/**
 * Splits a flat form data object into left and right column data objects.
 * Applies sensible defaults when a field is absent from the source data.
 *
 * @param {object} data - Raw/transformed form data from the API.
 * @returns {{ leftData: object, rightData: object }}
 *
 * Scale Analysis: O(n) over a fixed field list (LEFT_FIELDS + RIGHT_FIELDS).
 * Unaffected by data volume since field count is bounded and static.
 */
const splitFormData = (data = {}) => {
  const leftData = {};
  const rightData = {};

  LEFT_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(data, field)) {
      leftData[field] = data[field];
    } else {
      // Provide meaningful defaults per field type
      if (field === "researchCategories") leftData[field] = [];
      else if (field === "image_alias_preview") leftData[field] = null;
      else leftData[field] = "";
    }
  });

  RIGHT_FIELDS.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(data, field)) {
      rightData[field] = data[field];
    } else {
      if (
        field === "aka" ||
        field === "aka_typo_tolerance" ||
        field === "keywords"
      ) {
        rightData[field] = [];
      } else if (field === "isExplore" || field === "has_scholarship") {
        rightData[field] = false;
      } else if (field === "integrated_timeline_default_view") {
        rightData[field] = "lite";
      } else if (
        field === "home_collage_order" ||
        field === "explore_carousel_order" ||
        field === "explore_search_hint_order"
      ) {
        rightData[field] = null;
      } else {
        rightData[field] = "";
      }
    }
  });

  return { leftData, rightData };
};

/**
 * Merges left and right column form data back into a single flat object
 * for submission. Right-column values take precedence for overlapping keys.
 *
 * @param {object} leftData
 * @param {object} rightData
 * @returns {object} Combined form data.
 */
const combineFormData = (leftData, rightData) => ({
  ...leftData,
  ...rightData,
});

// ─── Component ────────────────────────────────────────────────────────────────
const Basic = () => {
  const { alias } = useParams();
  const toast = useToast();
  // Re-renders once the live hint pool size (app_configs) is known.
  useSearchHintPoolSize();

  // Preview / Edit mode from global Redux store (aligned with Location/Basic.js pattern)
  const previewEnabled = useSelector((state) => state.preview.isEnabled);

  // Separate state for two-column edit mode and single-column preview mode
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});

  // Original data snapshot used for computing deleted values on submit
  const [basicFieldData, setBasicFieldData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const homeCollageReassign = useHomeCollageReassign();

  // Form refs – used to programmatically trigger RJSF validation via Save button
  const leftFormRef = useRef(null);
  const rightFormRef = useRef(null);
  const combinedFormRef = useRef(null);

  // ── Options helpers ────────────────────────────────────────────────────────

  /**
   * Converts the raw research-categories API response into typeahead option objects.
   * @param {Array} data - Array of key-value pair arrays from the API.
   * @returns {Array<{label: string, value: string}>}
   */
  const transformOptions = (data) =>
    data.map((itemArray) => {
      const entity = itemArray.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {});
      return { label: entity.alias, value: entity.alias };
    });

  const fetchCategories = async (inputValue) => {
    const result = await getResearchCategoriesAPI();
    const transformedOptions = transformOptions(result);
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  // ── Data Transformation ────────────────────────────────────────────────────

  /**
   * Enriches raw API data with UI-only derived fields before splitting.
   * @param {object} data - Raw personality data from the API.
   * @returns {object} Transformed data ready for form state.
   */
  const transFormData = (data) => ({
    ...data,
    meta_title: data?.meta_title ?? "",
    meta_description: data?.meta_description ?? "",
    image_alias_preview: data?.image_alias || null,
    has_scholarship: data?.has_scholarship ?? false,
    isExplore: data?.isExplore ?? false,
    integrated_timeline_default_view:
      data?.integrated_timeline_default_view || "lite",
    aka: Array.isArray(data?.aka) ? data.aka : [],
    aka_typo_tolerance: Array.isArray(data?.aka_type_tolerance)
      ? data.aka_type_tolerance
      : [],
    keywords: Array.isArray(data?.keywords) ? data.keywords : [],
  });

  // ── isExplore flag (reactive across both modes) ────────────────────────────

  /**
   * Determines whether isExplore-gated fields should be rendered.
   * Reads from the active form state depending on the current mode.
   */
  const hasIsExplore = useMemo(() => {
    const dataToCheck = previewEnabled ? combinedFormData : rightFormData;
    return dataToCheck?.isExplore === true;
  }, [combinedFormData, rightFormData, previewEnabled]);

  // ── Schema memos (recomputed only when hasIsExplore changes) ──────────────
  const leftColumnSchema = useMemo(
    () => getLeftColumnSchema(hasIsExplore),
    [hasIsExplore],
  );
  const rightColumnSchema = useMemo(
    () => getRightColumnSchema(hasIsExplore),
    [hasIsExplore],
  );
  const combinedSchema = useMemo(
    () => getCombinedSchema(hasIsExplore),
    [hasIsExplore],
  );

  // ── UI schema memos ────────────────────────────────────────────────────────
  const leftColumnUiSchema = useMemo(
    () => getLeftColumnUiSchema(hasIsExplore)(isSubmitting),
    [hasIsExplore, isSubmitting],
  );
  const rightColumnUiSchema = useMemo(
    () => getRightColumnUiSchema(hasIsExplore)(isSubmitting),
    [hasIsExplore, isSubmitting],
  );
  const combinedUiSchema = useMemo(
    () => getCombinedUiSchema(hasIsExplore)(isSubmitting),
    [hasIsExplore, isSubmitting],
  );

  // ── Widgets ────────────────────────────────────────────────────────────────
  const widgets = {
    typeahead1: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchCategories} />
    ),
    typeahead3: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchMasterlistSubTypes} />
    ),
  };

  // ── Data Fetching ──────────────────────────────────────────────────────────
  useEffect(() => {
    const getBasicData = async () => {
      setIsLoading(true);
      try {
        if (alias) {
          const result = await getPersonalityBasicData(alias);
          if (result) {
            const transformedData = transFormData(result);
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
            setBasicFieldData(result);
          }
        } else {
          // Empty state for new records
          const { leftData, rightData } = splitFormData({});
          setLeftFormData(leftData);
          setRightFormData(rightData);
          setCombinedFormData({});
        }
      } catch (error) {
        console.error("Error fetching personality data:", error);
        showErrorToast(toast, "Failed to fetch personality data");
      } finally {
        setIsLoading(false);
      }
    };

    getBasicData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, toast, quickSave]);

  // ── Form Change Handlers ───────────────────────────────────────────────────
  const handleLeftFormChange = (data) => setLeftFormData(data.formData);
  const handleRightFormChange = (data) => setRightFormData(data.formData);
  const handleCombinedFormChange = (data) => setCombinedFormData(data.formData);

  // ── Submit ─────────────────────────────────────────────────────────────────
  /**
   * Gathers form data from the active mode, strips UI-only keys, and calls
   * the update API. Reloads data via quickSave toggle on success.
   */
  const handleSubmit = async () => {
    const formData = previewEnabled
      ? combinedFormData
      : combineFormData(leftFormData, rightFormData);

    // Guard: prevent submission when isExplore-required fields are missing
    if (hasIsExplore) {
      if (!formData.route_type || formData.route_type.trim() === "") return;
      if (!formData.image || formData.image.trim() === "") return;
    }

    const keysToRemove = ["image_alias_preview", "aka_typo_tolerance"];
    // Map form-state key (aka_typo_tolerance) back to API key (aka_type_tolerance)
    const mappedFormData = {
      ...formData,
      aka_type_tolerance: formData.aka_typo_tolerance,
    };
    const updatedFormData = removeKeysFromFormData(
      mappedFormData,
      keysToRemove,
    );
    const deletedValues = getDeletedValues(basicFieldData, updatedFormData);

    // Shared by the initial attempt and the confirm-reassign retry below --
    // extraPayload merges in confirm_home_collage_order_reassign: true only
    // when the admin has explicitly confirmed taking a taken slot (#266343).
    const submitPersonality = (extraPayload = {}) =>
      updatePersonalityBasicData(
        alias,
        { ...updatedFormData, ...extraPayload },
        deletedValues,
      );

    try {
      if (alias) {
        setIsSubmitting(true);
        const result = await submitPersonality();

        if (result?.success) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      const { conflict } = parseHomeCollageConflict(error);
      if (conflict) {
        // Let the admin confirm taking the slot instead of a dead-end error.
        // Confirming calls this same retry, with the flag set, so the slot
        // swap happens atomically on the backend in one call (#266343).
        homeCollageReassign.open(conflict, async () => {
          try {
            const result = await submitPersonality({
              confirm_home_collage_order_reassign: true,
            });
            if (result?.success) {
              setQuickSave((prev) => !prev);
              showSuccessToast(toast, "Data updated successfully");
            }
          } catch (retryError) {
            showErrorToast(
              toast,
              getApiErrorMessage(
                retryError,
                "Failed to update personality data",
              ),
            );
            throw retryError;
          }
        });
      } else {
        console.error("Error updating personality data:", error);
        showErrorToast(
          toast,
          getApiErrorMessage(error, "Failed to update personality data"),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* ── Single-column layout for Preview Mode ── */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <Box fontSize="lg" fontWeight="bold" mb={4}>
                  Primary fields
                </Box>
                <Form
                  ref={combinedFormRef}
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  onSubmit={(e) => {
                    if (!e.errors || e.errors.length === 0) {
                      handleSubmit();
                    }
                  }}
                  widgets={widgets}
                />
              </Box>
            </Box>
          ) : (
            /* ── Two-column layout for Edit Mode ── */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection={{ base: "column", md: "row" }}
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column – Identity & Classification */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Box fontSize="lg" fontWeight="bold" mb={4}>
                      Primary fields
                    </Box>
                    <Form
                      ref={leftFormRef}
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      onSubmit={() => {
                        // Left form submit is passive – validation only
                      }}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>

                  {/* Right Column – Metadata & Explore Fields */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      ref={rightFormRef}
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      onSubmit={(e) => {
                        if (!e.errors || e.errors.length === 0) {
                          handleSubmit();
                        }
                      }}
                      widgets={widgets}
                      fontsize={"14px"}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* ── Fixed Save Button at Bottom (matches Location/Basic.js pattern) ── */}
      <Box
        position="fixed"
        bottom={0}
        left="250px" // Offset by sidebar width
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            disabled={isSubmitting}
            onClick={() => {
              const forms = document.querySelectorAll("form");
              if (forms.length > 0) {
                forms.forEach((form) => {
                  const tempBtn = document.createElement("button");
                  tempBtn.type = "submit";
                  tempBtn.style.display = "none";
                  form.appendChild(tempBtn);
                  tempBtn.click();
                  form.removeChild(tempBtn);
                });
              } else {
                handleSubmit();
              }
            }}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      {/* Spacer to prevent content from sitting behind the fixed button */}
      <Box pb="100px" />

      <HomeCollageReassignDialog
        isOpen={homeCollageReassign.isOpen}
        conflict={homeCollageReassign.conflict}
        isConfirming={homeCollageReassign.isConfirming}
        cancelRef={homeCollageReassign.cancelRef}
        onConfirm={homeCollageReassign.confirm}
        onCancel={homeCollageReassign.close}
      />
    </Box>
  );
};

export default Basic;
